import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { Badge } from "@groot/ui/badge";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { Textarea } from "@groot/ui/textarea";
import { useCreditCardProfiles, useUpsertCreditCardProfile } from "@/hooks/api";
import { cn } from "@/lib/utils";
import { CircleCheck, CircleX, Pencil, Plus, Sparkles, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import type { CardBenefit, CreditCardProfileEntry, CreditCardStatus } from "@/types";

interface StringListEditorProps {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

function StringListEditor({ label, values, onChange, placeholder }: StringListEditorProps) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v || values.includes(v)) return;
    onChange([...values, v]);
    setDraft("");
  };
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="outline" size="sm" className="h-9" onClick={add}>
          <Plus className="size-4" />
        </Button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {values.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-xs"
            >
              {v}
              <button
                type="button"
                aria-label={`Remove ${v}`}
                onClick={() => onChange(values.filter((x) => x !== v))}
                className="text-muted-foreground/70 hover:text-foreground"
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

interface BenefitsEditorProps {
  benefits: CardBenefit[];
  onChange: (benefits: CardBenefit[]) => void;
}

function BenefitsEditor({ benefits, onChange }: BenefitsEditorProps) {
  const [draft, setDraft] = useState<CardBenefit>({ title: "" });

  const add = () => {
    const title = draft.title.trim();
    if (!title) return;
    onChange([...benefits, { ...draft, title }]);
    setDraft({ title: "" });
  };

  return (
    <div className="space-y-2">
      <Label>Benefits</Label>
      {benefits.length > 0 && (
        <div className="space-y-1.5">
          {benefits.map((b, i) => (
            <div
              key={`${b.title}-${i}`}
              className="flex items-start justify-between gap-2 rounded-md border border-border px-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-sm">{b.title}</p>
                {b.description && <p className="text-xs text-muted-foreground">{b.description}</p>}
                <div className="mt-1 flex flex-wrap gap-1">
                  {b.category && (
                    <Badge variant="outline" className="text-[10px]">
                      {b.category}
                    </Badge>
                  )}
                  {b.expiryDate && (
                    <Badge variant="outline" className="text-[10px]">
                      till {b.expiryDate.slice(0, 10)}
                    </Badge>
                  )}
                </div>
              </div>
              <button
                type="button"
                aria-label={`Remove ${b.title}`}
                onClick={() => onChange(benefits.filter((_, idx) => idx !== i))}
                className="text-muted-foreground/70 hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          value={draft.title}
          placeholder="e.g. 8 lounge visits/year"
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <Input
          value={draft.category ?? ""}
          placeholder="category"
          className="w-32"
          onChange={(e) => setDraft({ ...draft, category: e.target.value })}
        />
        <Button type="button" variant="outline" size="sm" className="h-9" onClick={add}>
          <Plus className="size-4" />
        </Button>
      </div>
    </div>
  );
}

interface ProfileEditorProps {
  entry: CreditCardProfileEntry;
  onDone: () => void;
}

function ProfileEditor({ entry, onDone }: ProfileEditorProps) {
  const upsert = useUpsertCreditCardProfile();
  const p = entry.profile;
  const [form, setForm] = useState({
    issuer: p?.issuer ?? "",
    network: p?.network ?? "",
    annualFee: p?.annualFee != null ? String(p.annualFee) : "",
    joiningFee: p?.joiningFee != null ? String(p.joiningFee) : "",
    interestRatePct: p?.interestRatePct != null ? String(p.interestRatePct) : "",
    interestFreeDays: p?.interestFreeDays != null ? String(p.interestFreeDays) : "",
    rewardRateNote: p?.rewardRateNote ?? "",
    notes: p?.notes ?? "",
  });
  const [bestFor, setBestFor] = useState<string[]>(p?.bestFor ?? []);
  const [notRecommendedFor, setNotRecommendedFor] = useState<string[]>(p?.notRecommendedFor ?? []);
  const [benefits, setBenefits] = useState<CardBenefit[]>(p?.benefits ?? []);

  const handleSave = async () => {
    await upsert.mutateAsync({
      accountId: entry.accountId,
      data: {
        issuer: form.issuer || null,
        network: form.network || null,
        annualFee: form.annualFee ? Number(form.annualFee) : null,
        joiningFee: form.joiningFee ? Number(form.joiningFee) : null,
        interestRatePct: form.interestRatePct ? Number(form.interestRatePct) : null,
        interestFreeDays: form.interestFreeDays ? Number(form.interestFreeDays) : null,
        rewardRateNote: form.rewardRateNote || null,
        bestFor,
        notRecommendedFor,
        benefits,
        notes: form.notes || null,
      },
    });
    onDone();
  };

  const numberField = (
    key: "annualFee" | "joiningFee" | "interestRatePct" | "interestFreeDays",
    label: string,
  ) => (
    <div className="space-y-1.5">
      <Label htmlFor={`cc-${key}`}>{label}</Label>
      <Input
        id={`cc-${key}`}
        type="number"
        min="0"
        step="0.01"
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
    </div>
  );

  return (
    <div className="space-y-4 rounded-lg border border-border bg-background p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="cc-issuer">Issuer</Label>
          <Input
            id="cc-issuer"
            value={form.issuer}
            placeholder="e.g. HDFC Bank"
            onChange={(e) => setForm({ ...form, issuer: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cc-network">Network</Label>
          <Input
            id="cc-network"
            value={form.network}
            placeholder="Visa / Mastercard / RuPay / Amex"
            onChange={(e) => setForm({ ...form, network: e.target.value })}
          />
        </div>
        {numberField("annualFee", "Annual fee")}
        {numberField("joiningFee", "Joining fee")}
        {numberField("interestRatePct", "Interest rate %")}
        {numberField("interestFreeDays", "Interest-free days")}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cc-reward">Reward rate note</Label>
        <Input
          id="cc-reward"
          value={form.rewardRateNote}
          placeholder="e.g. 5RP/₹150, 10x on partners"
          onChange={(e) => setForm({ ...form, rewardRateNote: e.target.value })}
        />
      </div>
      <StringListEditor
        label="Best for"
        values={bestFor}
        onChange={setBestFor}
        placeholder="e.g. online spends"
      />
      <StringListEditor
        label="Not recommended for"
        values={notRecommendedFor}
        onChange={setNotRecommendedFor}
        placeholder="e.g. fuel (no surcharge waiver)"
      />
      <BenefitsEditor benefits={benefits} onChange={setBenefits} />
      <div className="space-y-1.5">
        <Label htmlFor="cc-notes">Notes</Label>
        <Textarea
          id="cc-notes"
          rows={3}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} disabled={upsert.isPending}>
          Save profile
        </Button>
      </div>
    </div>
  );
}

export function CardProfilesPanel({ status = "active" }: { status?: CreditCardStatus }) {
  const { data: entries = [], isLoading } = useCreditCardProfiles(status);
  const [editingAccountId, setEditingAccountId] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[0, 1].map((i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
          <div key={i} className="h-32 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        No credit card accounts found. Add one under Accounts to start tracking its terms and
        benefits.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {entries.map((entry) => {
        const isEditing = editingAccountId === entry.accountId;
        const p = entry.profile;
        return (
          <div key={entry.accountId} className="rounded-lg border border-border bg-background">
            <div className="flex items-center justify-between border-b border-border bg-muted px-4 py-3">
              <Link to={`/accounts/${entry.accountId}`} className="flex items-center gap-2">
                {entry.account.iconUrl ? (
                  <img src={entry.account.iconUrl} alt="" className="size-5 rounded" />
                ) : null}
                <span className="text-sm font-medium">{entry.account.accountName}</span>
                {entry.account.isArchived && (
                  <span className="text-xs text-muted-foreground/70">(Archived)</span>
                )}
              </Link>
              <Button
                variant="ghost"
                size="sm"
                className="size-8 p-0"
                aria-label={p ? "Edit card profile" : "Add card profile"}
                onClick={() => setEditingAccountId(isEditing ? null : entry.accountId)}
              >
                {isEditing ? <X className="size-4" /> : <Pencil className="size-4" />}
              </Button>
            </div>

            {isEditing ? (
              <div className="p-4">
                <ProfileEditor entry={entry} onDone={() => setEditingAccountId(null)} />
              </div>
            ) : (
              <div className="space-y-3 p-4">
                {!p ? (
                  <p className="text-sm text-muted-foreground">
                    No profile yet — track this card's terms, benefits, and best-use notes.
                  </p>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {p.issuer && <span>Issuer: {p.issuer}</span>}
                      {p.network && <span>{p.network}</span>}
                      {p.annualFee != null && (
                        <span>
                          Fee: <AmountDisplay amount={p.annualFee} />
                          /yr
                        </span>
                      )}
                      {p.interestRatePct != null && <span>{p.interestRatePct}% p.a.</span>}
                      {p.interestFreeDays != null && (
                        <span>{p.interestFreeDays} interest-free days</span>
                      )}
                    </div>
                    {p.rewardRateNote && (
                      <p className="inline-flex items-center gap-1.5 text-sm">
                        <Sparkles className="size-3.5 text-muted-foreground" />
                        {p.rewardRateNote}
                      </p>
                    )}
                    {p.bestFor && p.bestFor.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-success">
                          <CircleCheck className="size-3.5" /> Best for
                        </span>
                        {p.bestFor.map((v) => (
                          <Badge
                            key={v}
                            className="border-success/30 bg-success/10 text-success hover:bg-success/10"
                          >
                            {v}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {p.notRecommendedFor && p.notRecommendedFor.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 text-xs font-medium text-destructive",
                          )}
                        >
                          <CircleX className="size-3.5" /> Not recommended for
                        </span>
                        {p.notRecommendedFor.map((v) => (
                          <Badge
                            key={v}
                            className="border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/10"
                          >
                            {v}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {p.benefits && p.benefits.length > 0 && (
                      <div className="space-y-1">
                        {p.benefits.map((b) => (
                          <div
                            key={b.title}
                            className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-1.5"
                          >
                            <span className="text-sm">{b.title}</span>
                            <span className="flex items-center gap-1.5">
                              {b.category && (
                                <Badge variant="outline" className="text-[10px]">
                                  {b.category}
                                </Badge>
                              )}
                              {b.expiryDate && (
                                <Badge variant="outline" className="text-[10px]">
                                  till {b.expiryDate.slice(0, 10)}
                                </Badge>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {p.notes && <p className="text-xs text-muted-foreground">{p.notes}</p>}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
